import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import type { OnboardingDraft } from './types';

type ExperienceItem = NonNullable<OnboardingDraft['workExperience']>[number];

type ExperienceEditModalProps = {
  show: boolean;
  items: ExperienceItem[];
  saving?: boolean;
  onHide: () => void;
  onSave: (items: ExperienceItem[]) => Promise<boolean>;
};

export default function ExperienceEditModal({
  show,
  items,
  saving = false,
  onHide,
  onSave,
}: ExperienceEditModalProps) {
  const [draftItems, setDraftItems] = useState<ExperienceItem[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setDraftItems(
        items.length
          ? items.map((item) => ({ ...item }))
          : [{ title: '', company: '', startDate: '', endDate: '', description: '' }]
      );
    }
  }, [show, items]);

  const updateItem = (index: number, patch: Partial<ExperienceItem>) => {
    setDraftItems((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
    );
  };

  const addItem = () => {
    setDraftItems((prev) => [
      ...prev,
      { title: '', company: '', startDate: '', endDate: '', description: '' },
    ]);
  };

  const removeItem = (index: number) => {
    setDraftItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleSave = async () => {
    const cleaned = draftItems.filter(
      (item) => (item.title || '').trim() || (item.company || '').trim()
    );
    setSubmitting(true);
    try {
      const saved = await onSave(cleaned);
      if (saved) {
        onHide();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const busy = saving || submitting;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="ss-app-modal">
      <Modal.Header closeButton>
        <Modal.Title>Edit experience</Modal.Title>
      </Modal.Header>
      <Modal.Body className="ssr-modal-body">
        {draftItems.map((item, index) => (
          <div className="ssr-exp-edit-block" key={`exp-edit-${index}`}>
            <div className="ssr-card-head">
              <p className="ssr-exp-edit-label">Role {index + 1}</p>
              {draftItems.length > 1 ? (
                <button type="button" className="ssr-edit-link" onClick={() => removeItem(index)}>
                  Remove
                </button>
              ) : null}
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Job title</Form.Label>
              <Form.Control
                className="ssr-field-input"
                value={item.title || ''}
                onChange={(event) => updateItem(index, { title: event.target.value })}
                placeholder="e.g. Senior Fullstack Engineer"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Company</Form.Label>
              <Form.Control
                className="ssr-field-input"
                value={item.company || ''}
                onChange={(event) => updateItem(index, { company: event.target.value })}
                placeholder="Company name"
              />
            </Form.Group>
            <div className="ssr-exp-edit-dates">
              <Form.Group className="mb-3">
                <Form.Label>Start date</Form.Label>
                <Form.Control
                  className="ssr-field-input"
                  value={item.startDate || ''}
                  onChange={(event) => updateItem(index, { startDate: event.target.value })}
                  placeholder="e.g. Mar 2024"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>End date</Form.Label>
                <Form.Control
                  className="ssr-field-input"
                  value={item.endDate || ''}
                  onChange={(event) => updateItem(index, { endDate: event.target.value })}
                  placeholder="e.g. Present"
                />
              </Form.Group>
            </div>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                className="ssr-field-input ssr-field-textarea ssr-field-textarea--lg"
                value={item.description || ''}
                onChange={(event) => updateItem(index, { description: event.target.value })}
                placeholder="Responsibilities and achievements (one per line)"
              />
            </Form.Group>
          </div>
        ))}
        <Button variant="outline-success" type="button" onClick={addItem}>
          Add another role
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button variant="success" onClick={() => void handleSave()} disabled={busy}>
          {busy ? 'Saving…' : 'Save experience'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
